// Community inbox — the quick actions on a middle-pane thread row (Scott,
// 2026-09-30), and the two things the row and the detail pane must share so
// they can never disagree. Pure, so every rule is asserted without jsdom.

import type { InboxChannel, InboxKind, InboxMessage, InboxStatus } from '../types/inbox.js';

export type RowAction = 'hide' | 'delete' | 'resolve';

/**
 * Which quick actions a thread row offers. THE ONE PLACE this is decided —
 * two components deciding it separately is how a Delete ends up on a review.
 *
 * Scott's ruling, per kind:
 *   comment → Hide · Delete · Resolve
 *   review  → Resolve   (deleting does nothing on a review — there is no
 *                        platform delete for a customer's review)
 *   dm      → Resolve
 *
 * Resolve is offered only while the thread is unresolved (the existing
 * quick-resolve rule). Our own top-level comments never reach the feed.
 */
export function rowActionsForKind(kind: InboxKind, status: InboxStatus): RowAction[] {
  const resolve: RowAction[] = status === 'resolved' ? [] : ['resolve'];
  if (kind === 'comment') return ['hide', 'delete', ...resolve];
  return resolve;
}

/**
 * The message a comment thread's moderation actions target: the customer's
 * comment itself — the first INCOMING message that carries a platform id.
 *
 * ⚠️ Shared by the row's Hide/Delete AND the detail pane's default reply
 * target, so the row can never hide something different from what the panel
 * would. Hide is PER MESSAGE (inboxMessage.hidden), never per thread — a
 * thread-level flag once rendered every comment in a post as hidden.
 * null when the thread has no actionable body (e.g. a media-only comment whose
 * text was never ingested).
 */
export function commentBodyMessage<M extends Pick<InboxMessage, 'direction' | 'externalId'>>(
  messages: readonly M[],
): M | null {
  return messages.find((m) => m.direction === 'incoming' && !!m.externalId) ?? null;
}

/**
 * The confirm shown before deleting a comment on the platform. ONE string for
 * the row and the detail pane: retyped, the two drift, and one day one of
 * them stops asking.
 */
export const DELETE_COMMENT_CONFIRM = 'Delete this comment on the platform? This cannot be undone.';

/**
 * Whether a thread offers HIDE and DELETE on its comments at all. ONE definition,
 * read by the staff pane, the club pane AND the server routes, so a button that
 * is not drawn is also an action the server refuses (a hidden button is not a
 * control; the route is).
 *
 * Only comments on Facebook, Instagram and Facebook ads can be moderated. Google
 * Business has no comments at all (its threads are reviews, and a customer's
 * review cannot be hidden or deleted by the business), and a DM is not a comment.
 */
const MODERATED_COMMENT_CHANNELS: ReadonlySet<InboxChannel> = new Set<InboxChannel>([
  'facebook',
  'instagram',
  'metaads',
]);

export function commentModerationOffered(channel: InboxChannel, kind: InboxKind): boolean {
  return kind === 'comment' && MODERATED_COMMENT_CHANNELS.has(channel);
}

/**
 * The confirm shown to a CLUB before it deletes a member's comment. Deleting is
 * permanent on the platform, so it NAMES the author and QUOTES the comment: a
 * club must be able to see exactly which comment is about to go. Pure, so the
 * wording is asserted without jsdom. The quote is capped so a very long comment
 * cannot push the buttons off screen.
 */
export const DELETE_QUOTE_MAX = 280;

export function deleteCommentConfirmCopy(
  authorName: string,
  text: string,
): { title: string; quote: string; warning: string } {
  const name = authorName.trim() || 'this person';
  const body = text.trim();
  const quote = body.length > DELETE_QUOTE_MAX ? `${body.slice(0, DELETE_QUOTE_MAX).trimEnd()}...` : body;
  return {
    title: `Delete ${name}'s comment?`,
    quote: quote === '' ? '(This comment has no text, only media.)' : quote,
    warning: 'It will be removed from the page for everyone. This cannot be undone.',
  };
}
